import {
  Employee,
  PerDiemKind,
  PerDiemRate,
  Project,
  TaskRate,
} from "~/gql/types";
import { ExpenseRate } from "~/gql/ExpenseRate";
import { GetEmployeeRate, GetPerDiemRate } from "../types";

type RateAmountMatchProps = {
  matchingRate: TaskRate | ExpenseRate;
  rateAmountFieldValue: string | null;
  asOf: string;
  project: Project;
  employee: Employee | null;
  getPerDiemRate: GetPerDiemRate;
  getEmployeeRate: GetEmployeeRate;
};

/**
 * The amount the matched rate yields, as the `rate-sheet-resolution` capability
 * defines it, or null when there is nothing to set. Mirrors the old form's
 * `project-charge-form-old/useSetRateAmount.ts`.
 */
const yieldedAmount = async (
  props: RateAmountMatchProps
): Promise<string | null> => {
  const {
    matchingRate,
    asOf,
    project,
    employee,
    getPerDiemRate,
    getEmployeeRate,
  } = props;

  switch (matchingRate.__typename) {
    case "TaskRate": {
      if (!matchingRate.usesEmployeeRate) {
        return `${matchingRate.billableAmount}`;
      }

      if (!employee) {
        return `${0}`;
      }

      const employeeRate = await getEmployeeRate(
        employee.userPrincipalName,
        asOf
      );
      return `${employeeRate ?? 0}`;
    }
    case "AtvExpenseRate":
    case "MobileAllowanceExpenseRate":
      return `${matchingRate.billableAmount}`;
    case "MileageExpenseRate":
      return `${matchingRate.billableAmountPerMile}`;
    case "PerDiemExpenseRate": {
      if (matchingRate.billableOverride != null) {
        return `${matchingRate.billableOverride}`;
      }

      if (!project.location) {
        return null;
      }

      const { state, county } = project.location;
      const perDiemRate = await getPerDiemRate(asOf, state, county);
      if (!perDiemRate) {
        return null;
      }

      return `${perDiemAmount(perDiemRate, matchingRate.kind)}`;
    }
    default:
      return null;
  }
};

const perDiemAmount = (perDiemRate: PerDiemRate, kind: PerDiemKind): number => {
  switch (kind) {
    case PerDiemKind.Lodging:
      return perDiemRate.lodgingBillable;
    case PerDiemKind.Meals:
      return perDiemRate.mealsBillable;
    case PerDiemKind.Travel:
    case PerDiemKind.TravelMeals:
      return perDiemRate.travelBillable;
  }
};

export const rateAmountMatch = async (
  props: RateAmountMatchProps
): Promise<string | null> => {
  const rateAmount = await yieldedAmount(props);

  if (rateAmount === null || rateAmount === props.rateAmountFieldValue) {
    return null;
  }

  return rateAmount;
};
